import { Fragment, useRef } from 'react';

import {
  CellLayout,
  DotBadge,
  EmptyState,
  InteractiveTable,
  ListLayout,
  LoadingTableRows,
  MoreButton,
  Table,
} from '@ingrid/luterkort-react';
import { ShippingLogo } from '@ingrid/react-shipping-logo';

import { Ellipsis } from '~src/components/Ellipsis/Ellipsis';
import { TableAddressSecond } from '~src/components/TableAddress/TableAddress';
import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';
import { Format } from '~src/utils/format';
import { getStatusColor, getStatusLabel } from '~src/utils/status-helpers';

import { DirectionTypeIcon } from '../details/direction-type';
import { ShipmentListItem } from '../queries';
import { ActionPanel } from './action-panel';
import { DetailsDrawer } from './details-drawer';
import { Menu } from './menu';

interface TableProps {
  isEmpty: boolean;
  isLoading: boolean;
  loadRowCount: number;
  shipments: ShipmentListItem[];
}

export function ListTable(props: TableProps) {
  const { isEmpty, isLoading, loadRowCount, shipments } = props;
  const tableRef = useRef<HTMLTableElement>(null);

  const { highlightedKey } = InteractiveTable.useContext();
  const { t } = useTranslation();

  const shipment = shipments.find(s => s.id === highlightedKey);

  if (isEmpty) {
    return <EmptyState title={t('shipments.list.noneFound')} />;
  }

  return (
    <Fragment>
      <InteractiveTable.Table ref={tableRef}>
        <thead>
          <Table.Row>
            <Table.HeadCell css={{ width: '35px' }}>
              <InteractiveTable.HeadCheckbox />
            </Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>{t('shipments.list.ssccId')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>{t('common.status')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '17.5%' }}>{t('common.shipFrom')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '17.5%' }}>{t('common.shipTo')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '15%' }}>{t('common.carrierProduct')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '10%' }}>{t('common.shippingDate')}</Table.HeadCell>
            <Table.HeadCell css={{ width: '35px' }} />
          </Table.Row>
        </thead>
        <tbody>
          {isLoading ? (
            <LoadingTableRows tableRef={tableRef} rowCount={loadRowCount} />
          ) : (
            shipments.map(shipment => (
              <InteractiveTable.Row key={shipment.id} value={shipment.id!}>
                <Table.Cell>
                  <InteractiveTable.Checkbox value={shipment.id!} />
                </Table.Cell>
                <Table.Cell>
                  <ListLayout.Column css={{ gap: 0 }}>
                    <ListLayout.Row css={{ gap: '8px' }}>
                      <Ellipsis>{Format.Id(shipment.id ?? '') ?? '-'}</Ellipsis>
                      <DirectionTypeIcon direction={shipment.direction_type!} withTooltip />
                    </ListLayout.Row>
                    {shipment.tracking_numbers && shipment.tracking_numbers.length > 0 && (
                      <TableAddressSecond>{shipment.tracking_numbers}</TableAddressSecond>
                    )}
                  </ListLayout.Column>
                </Table.Cell>
                <Table.Cell>
                  <DotBadge variant={getStatusColor(shipment.status)} style={{ width: 'auto' }}>
                    <Ellipsis>{t(getStatusLabel(shipment.status))}</Ellipsis>
                  </DotBadge>
                </Table.Cell>
                <Table.Cell>
                  <Ellipsis>{shipment.address_from?.name}</Ellipsis>
                  <TableAddressSecond>{shipment.address_from?.city}</TableAddressSecond>
                </Table.Cell>
                <Table.Cell>
                  <Ellipsis>{shipment.address_to?.name}</Ellipsis>
                  <TableAddressSecond>{shipment.address_to?.city}</TableAddressSecond>
                </Table.Cell>
                <Table.Cell>
                  <CellLayout.Row>
                    <ShippingLogo method={shipment.shipping_method?.id ?? ''} width={32} />
                    <Ellipsis>{shipment.shipping_method?.name}</Ellipsis>
                  </CellLayout.Row>
                </Table.Cell>
                <Table.Cell>
                  <LocalizedDate dateTime={shipment.shipping_date!} />
                </Table.Cell>
                <Table.Cell>
                  <Menu shipment={shipment} trigger={<MoreButton size="sm" />} />
                </Table.Cell>
              </InteractiveTable.Row>
            ))
          )}
        </tbody>
      </InteractiveTable.Table>

      <ActionPanel />

      <DetailsDrawer shipment={shipment} />
    </Fragment>
  );
}
